import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle, WarningCircle, Info, X } from '@phosphor-icons/react';

// Shared success/failure alert for the Supporting Tools screens.
//
// Rendered through a portal into <body> and pinned to the viewport, so the alert
// is visible wherever the user is scrolled to. Previously each screen rendered its
// own copy inline above the form, which pushed the alert off-screen when a long
// form was submitted from the bottom.
//
// Two modes, chosen by `toast.sticky`:
//   sticky  -> the outcome of a save/delete. Dims and blocks the screen until the
//              user acknowledges it, so a result can never be missed or scrolled
//              past. Closes on the X, Esc, or a click on the dimmed area.
//   passing -> field-validation and lookup warnings. Non-blocking and click-through;
//              the caller fades these out on its own timer.
const PALETTE = {
  success: { bg: '#ECFDF5', border: '#A7F3D0', text: '#065F46', icon: <CheckCircle size={20} color="#059669" weight="fill" /> },
  info: { bg: '#EFF4FF', border: '#BFDBFE', text: '#1D4ED8', icon: <Info size={20} color="#2563EB" weight="fill" /> },
  warn: { bg: '#FFFBEB', border: '#FDE68A', text: '#92400E', icon: <WarningCircle size={20} color="#D97706" weight="fill" /> },
  error: { bg: '#FEF2F2', border: '#FECACA', text: '#991B1B', icon: <WarningCircle size={20} color="#DC2626" weight="fill" /> },
};

function Toast({ toast, onClose, testId = 'supporting-tools-toast' }) {
  const blocking = !!toast && !!toast.sticky;

  // Declared before the early return below — hooks cannot run conditionally.
  useEffect(() => {
    if (!blocking) return undefined;
    const onKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [blocking, onClose]);

  if (!toast) return null;
  const palette = PALETTE[toast.severity] || PALETTE.info;
  const isProblem = toast.severity === 'error' || toast.severity === 'warn';

  const card = (
    <div
      style={{
        display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '12px 14px',
        border: `1px solid ${palette.border}`, background: palette.bg, borderRadius: '8px',
        boxShadow: '0 6px 20px rgba(0, 0, 0, 0.12)',
        animation: 'st-toast-in 160ms ease-out',
        pointerEvents: 'auto',
        width: 'max-content', minWidth: '320px', maxWidth: 'min(560px, calc(100vw - 32px))',
      }}
      role={blocking ? 'alertdialog' : (isProblem ? 'alert' : 'status')}
      aria-modal={blocking ? 'true' : undefined}
      aria-live={blocking ? undefined : (isProblem ? 'assertive' : 'polite')}
      data-testid={testId}
    >
      {palette.icon}
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '13px', fontWeight: '600', color: palette.text }}>{toast.summary}</div>
        <div style={{ fontSize: '12px', color: palette.text, marginTop: '2px' }}>{toast.detail}</div>
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label="Dismiss notification"
        autoFocus={blocking}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: palette.text }}
        data-testid={`${testId}-close`}
      >
        <X size={14} />
      </button>
    </div>
  );

  const keyframes = <style>{'@keyframes st-toast-in { from { opacity: 0; transform: translateY(-12px); } to { opacity: 1; transform: translateY(0); } }'}</style>;

  if (blocking) {
    return createPortal(
      <div
        style={{
          position: 'fixed', inset: 0, zIndex: 10000,
          background: 'rgba(15, 23, 42, 0.45)',
          display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: '16px',
        }}
        // Only a click that lands on the backdrop itself dismisses — a click that
        // started inside the card and drifted out must not close it.
        onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
        data-testid={`${testId}-backdrop`}
      >
        {keyframes}
        {card}
      </div>,
      document.body,
    );
  }

  return createPortal(
    <div
      style={{
        position: 'fixed', top: '16px', left: '50%', transform: 'translateX(-50%)',
        zIndex: 10000,
        // The wrapper must not swallow clicks aimed at the page behind it.
        pointerEvents: 'none',
      }}
    >
      {keyframes}
      {card}
    </div>,
    document.body,
  );
}

export default Toast;
